import React from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { fixtureLabel, groupProducts, pieces, RestockLine, RestockPlan, variantLabel } from './restockPlan';

interface WorklistProps {
  step: number;
  title: string;
  note: string;
  empty: string;
  lines: RestockLine[];
  plan: RestockPlan;
  copyLabel?: string;
  onCopy?: () => void;
  children?: React.ReactNode;
}

export const ProductLines: React.FC<{ lines: RestockLine[]; plan: RestockPlan }> = ({ lines, plan }) => (
  <Box>
    {groupProducts(lines).map((product) => {
      const first = product[0];
      const reasons = Array.from(new Set(product.map((line) => line.reason).filter(Boolean)));
      const evidence = Array.from(new Set(product.map((line) => line.evidence_note).filter((note): note is string => !!note && !reasons.includes(note))));
      return (
        <Box key={`${first.vendor}:${first.sku}`} sx={{ py: 2, display: 'flex', gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {first.image_url ? (
            <Box component="img" src={first.image_url} alt={first.title} loading="lazy"
              sx={{ width: 64, height: 84, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
          ) : <Box sx={{ width: 64, height: 84, bgcolor: 'action.hover', borderRadius: 1, flexShrink: 0, display: 'grid', placeItems: 'center' }}><Typography variant="caption" color="text.secondary">無圖片</Typography></Box>}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={700}>{first.sku} · {first.title.replace(/^\[Metanoia\]\s*/i, '')}</Typography>
              {first.evidence_label && <Chip size="small" variant="outlined" label={first.evidence_label} />}
            </Box>
            {product.map((line, index) => (
              <Box key={`${line.variant_id}:${index}`} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'baseline', mt: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>{variantLabel(line.color, line.size)} × {line.qty} 件</Typography>
                <Typography variant="caption" color="text.secondary">放置：{fixtureLabel(line, plan)}</Typography>
              </Box>
            ))}
            {reasons.map((reason) => (
              <Typography key={reason} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{reason}</Typography>
            ))}
            {evidence.length > 0 && <Box component="details" sx={{ mt: 0.75 }}>
              <Typography component="summary" variant="caption" sx={{ cursor: 'pointer', color: 'primary.main', width: 'fit-content' }}>查看依據</Typography>
              {evidence.map((note) => <Typography key={note} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{note}</Typography>)}
            </Box>}
          </Box>
          <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{pieces(product)} 件</Typography>
        </Box>
      );
    })}
  </Box>
);

const RestockWorklist: React.FC<WorklistProps> = ({ step, title, note, empty, lines, plan, copyLabel, onCopy, children }) => (
  <Paper component="section" id={`noke-restock-step-${step}`} aria-labelledby={`noke-restock-heading-${step}`} tabIndex={-1}
    sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, scrollMarginTop: 24 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
      <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%', fontSize: 14 }}>{step}</Box>
      <Typography component="h2" variant="h6" id={`noke-restock-heading-${step}`}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{pieces(lines)} 件</Typography>
      {onCopy && lines.length > 0 && <Button variant="outlined" size="small" onClick={onCopy} sx={{ ml: 'auto' }}>{copyLabel || '複製清單'}</Button>}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{note}</Typography>
    {lines.length ? children || <ProductLines lines={lines} plan={plan} /> : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{empty}</Typography>}
  </Paper>
);

export default RestockWorklist;
